import { useCallback, useEffect, useMemo } from 'react';

import { atom, useRecoilState } from 'recoil';

import { fetcher } from '@shared/hooks/recoil-query';
import { useAuth } from '@shared/state/hooks/useAuth';
import { UserRole } from '@shared/types';
import { useQuery } from 'react-query';

import { userEnquiryListHookUrl } from './enquiry.hook';
import { Enquiry, EnquiryStatus, UserEnquiryList } from './enquiry.interface';

//* 백엔드 `/non-admin/enquiry`의 @Roles와 **반드시 같은 목록**. RolesGuard가 권한 부족에 403이
//* 아닌 401을 던지고 axios 인터셉터가 "접근 권한이 없습니다" 알림을 띄우므로, 목록에 없는
//* 역할은 아예 조회하지 않는다 (메뉴 배지용 훅이라 콘솔 페이지마다 알림이 뜨는 사고 방지).
//* BugoAgencyBranchAdmin·GiftAgent·GiftBrandSeller는 백엔드 079caed(2026-09-04)에서 열렸다 —
//* 그 백엔드가 먼저 배포돼야 한다 (docs/enquiry-non-admin-roles-spec.md).
export const MY_ENQUIRY_READ_ROLES: readonly UserRole[] = [
  UserRole.BugoAgencyAdmin,
  UserRole.BugoAgencyBranchAdmin,
  UserRole.BugoAgencyWorker,
  UserRole.BugoFuneralHome,
  UserRole.BugoCustomer,
  UserRole.BugoGuest,
  UserRole.StoreSeller,
  UserRole.StoreCustomer,
  UserRole.StoreGuest,
  UserRole.GiftAgent,
  UserRole.GiftBrandSeller,
];

//* "답변 읽음" 시각 — 기기별 localStorage(사용자별 키). 메뉴 배지와 내역 탭이 같은 값을 봐야
//* 하므로 recoil로 공유하고, 저장소는 최초 1회만 읽는다.
type SeenAtByUser = Record<string, number>;
const answerSeenAtAtom = atom<SeenAtByUser>({
  key: 'enquiryAnswerSeenAtAtom',
  default: {},
});
const seenStorageKey = (userId: string) => `enquiry:answerSeenAt:${userId}`;

const readSeenAt = (userId: string): number => {
  try {
    const raw = localStorage.getItem(seenStorageKey(userId));
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
};
const writeSeenAt = (userId: string, at: number) => {
  try {
    localStorage.setItem(seenStorageKey(userId), String(at));
  } catch {
    // 저장 불가 환경(시크릿 등)은 세션 내 recoil 값으로만 동작
  }
};

//* axios 응답 인터셉터가 ISO 문자열을 Date로 바꿔놓으므로 둘 다 받는다
const toMs = (v: Date | string | undefined) => (v ? new Date(v).getTime() : 0);

const ownerId = (user: Enquiry['user'] | { _id: string } | null | undefined) =>
  typeof user === 'string' ? user : user?._id ?? '';

export const hasAnswer = (e: Enquiry<unknown, unknown>) =>
  e.status === EnquiryStatus.Done || !!(e.answer && e.answer.trim());

/**
 * 내 문의 내역 + 안 읽은 답변 수. 문의 패널(탭)과 메뉴 배지가 같이 쓴다.
 * - 조회는 로그인 + 허용 역할일 때만 (위 주석). 관리자는 /admin 목록이 내려오므로 본인 것만 거른다.
 * - 창 포커스 refetch는 끈다 — 메뉴에 붙는 훅이라 페이지마다 재조회하면 부하만 는다.
 *   접수 직후 갱신은 폼의 invalidateQueriesByUrlPart('enquiry')가 담당한다.
 */
export const useMyEnquiries = () => {
  const { userProfile, guestAuthenticated, assetRole, isAdmin } = useAuth();
  const userId = userProfile?._id ?? '';
  const roles = userProfile?.roles ?? [];

  const canRead =
    guestAuthenticated &&
    !!userId &&
    (isAdmin() || roles.some((r) => MY_ENQUIRY_READ_ROLES.includes(r)));

  const { data, status } = useQuery<UserEnquiryList>(
    userEnquiryListHookUrl(assetRole()),
    fetcher,
    { enabled: canRead, refetchOnWindowFocus: false, staleTime: 60 * 1000 },
  );

  const list = useMemo(
    () => (data ?? []).filter((e) => ownerId(e.user) === userId),
    [data, userId],
  );

  const [seenAtByUser, setSeenAtByUser] = useRecoilState(answerSeenAtAtom);
  useEffect(() => {
    if (!userId || userId in seenAtByUser) return;
    setSeenAtByUser((prev) => ({ ...prev, [userId]: readSeenAt(userId) }));
  }, [userId, seenAtByUser, setSeenAtByUser]);
  const seenAt = seenAtByUser[userId] ?? 0;

  const unreadIds = useMemo(
    () =>
      new Set(
        list.filter((e) => hasAnswer(e) && toMs(e.updatedAt) > seenAt).map((e) => e._id),
      ),
    [list, seenAt],
  );

  const markSeen = useCallback(() => {
    if (!userId) return;
    const now = Date.now();
    writeSeenAt(userId, now);
    setSeenAtByUser((prev) => ({ ...prev, [userId]: now }));
  }, [userId, setSeenAtByUser]);

  return {
    canRead,
    status: canRead ? status : ('idle' as const),
    list,
    unreadIds,
    unreadCount: unreadIds.size,
    markSeen,
  };
};
